'use client';

import { useEffect, useState } from 'react';

interface AdditionalInfoFormProps {
  dictionary: any;
  onSubmit: (additionalDetails: string) => void;
  disableSubmit?: boolean;
  // Prefill for session-resume
  initialChoice?: boolean; // true yes, false no
  initialDetails?: string;
}

export default function AdditionalInfoForm({
  dictionary,
  onSubmit,
  disableSubmit = false,
  initialChoice,
  initialDetails
}: AdditionalInfoFormProps) {
  const [wantToAddDetails, setWantToAddDetails] = useState<boolean | null>(null);
  const [additionalDetails, setAdditionalDetails] = useState('');

  // Prefill
  useEffect(() => {
    try {
      if (typeof initialChoice === 'boolean') setWantToAddDetails(initialChoice);
      if (typeof initialDetails === 'string') setAdditionalDetails(initialDetails);
    } catch (_) {}
  }, [initialChoice, initialDetails]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Submit the details if 'Yes' was chosen, otherwise submit an empty string
    onSubmit(wantToAddDetails ? additionalDetails.trim() : '');
  };

  return (
    // Use full width and let parent control bottom margin
    <form onSubmit={handleSubmit} className="w-full">
      {/* Question Text */}
      <p className="text-base md:text-sm text-zinc-800">
        {dictionary?.sellingForm?.additionalInfoQuestion ||
          'To help refine the valuation, would you like to add details about features like a pool, terrace, solar panels, renovations, etc.?'}
      </p>

      {/* Yes/No Radio Buttons */} 
      <div className="mt-4 flex items-center gap-2">
        {/* Yes Option */}
        <label
          htmlFor="additionalInfoYes"
          className="relative flex-1 flex items-center justify-center cursor-pointer p-3 border rounded-lg hover:bg-zinc-50 transition-colors has-[:checked]:border-zinc-800 has-[:checked]:bg-zinc-50 has-[:checked]:ring-2 has-[:checked]:ring-zinc-800"
        >
          <input
            type="radio"
            id="additionalInfoYes"
            name="wantToAddDetails"
            value="yes"
            checked={wantToAddDetails === true}
            onChange={() => setWantToAddDetails(true)}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
          <span className="text-base md:text-sm text-zinc-800 font-medium flex items-center gap-2">
            {dictionary?.sellingForm?.yes || 'Yes'}
            {wantToAddDetails === true && (
              <svg className="w-4 h-4 text-black flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            )}
          </span>
        </label>

        {/* No Option */}
        <label
          htmlFor="additionalInfoNo"
          className="relative flex-1 flex items-center justify-center cursor-pointer p-3 border rounded-lg hover:bg-zinc-50 transition-colors has-[:checked]:border-zinc-800 has-[:checked]:bg-zinc-50 has-[:checked]:ring-2 has-[:checked]:ring-zinc-800"
        >
          <input
            type="radio"
            id="additionalInfoNo"
            name="wantToAddDetails"
            value="no"
            checked={wantToAddDetails === false}
            onChange={() => setWantToAddDetails(false)}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
          <span className="text-base md:text-sm text-zinc-800 font-medium flex items-center gap-2">
            {dictionary?.sellingForm?.no || 'No'}
            {wantToAddDetails === false && (
              <svg className="w-4 h-4 text-black flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            )}
          </span>
        </label>
      </div>

      {/* Conditional Textarea (appears when 'Yes' is selected) */}
      {wantToAddDetails === true && (
        <textarea
          value={additionalDetails}
          onChange={(e) => setAdditionalDetails(e.target.value)}
          placeholder={
            dictionary?.sellingForm?.additionalDetailsPlaceholder ||
            'E.g., Recent kitchen renovation (2022), solar panels installed, large south-facing terrace...'
          }
          // Combine styles: Base input + container's border/rounding/bg + focus ring
          className="mt-2 w-full px-3 py-2 text-base md:text-sm bg-white border border-zinc-300 rounded-lg focus:ring-2 focus:ring-zinc-800 focus:border-zinc-800 outline-none transition-shadow min-h-[100px]"
        />
      )}

      {/* Submit Button (appears when a choice is made) */}
      {wantToAddDetails !== null && (
        <div className="mt-4">
          <button
            type="submit"
            disabled={disableSubmit}
            // Apply button classes using constants
            className="px-4 py-2.5 bg-zinc-800 text-white rounded-lg text-sm font-medium transition-colors hover:bg-zinc-700 disabled:opacity-50 disabled:pointer-events-none"
          >
            {dictionary?.sellingForm?.continueButton || 'Continue'}
          </button>
        </div>
      )}
    </form>
  );
} 